import type { DataFindingRow, DataProjectHealthRow, Json } from '../../lib/database.types'
import { arr, obj, str } from '../../lib/json'
import { toReconMap, type ReconMap } from './recon'

/**
 * What a department's checks found, and what happened to it since.
 *
 * A finding is one problem one check found in one project, tracked from the night it
 * first failed to the night the check passed (98h-infra/sql/fleet/20260923010000_data_findings.sql).
 * Nobody marks a finding fixed: the check that found it is what clears it. So this
 * mapper never infers a state from anything but the row -- it translates the four the
 * table can express into words and an order, and nothing more.
 *
 *   open      the check still finds it, and nobody has said it is expected
 *   accepted  the check still finds it, and its queue item was dismissed: a person
 *             judged it expected. Not waiting on anyone, so not in the open list.
 *   cleared   the check ran and found nothing
 *   retired   the check stopped being reported. Not evidence of a fix, and the page
 *             must not read it as one.
 *
 * Severity is shown, never shouted (product rule 1). The critical and high rows take the
 * muted clay of the failure family, medium the muted ochre of waiting, and nothing is
 * filled, badged or counted in a colour.
 */

export type FindingState = 'open' | 'accepted' | 'cleared' | 'retired'

export interface FindingObject {
  name: string
  grantee: string | null
  severity: string | null
  hint: string | null
}

export interface FindingTicket {
  ref: string
  url: string | null
  /** Linear's state *type* as the ticketer last saw it -- backlog, started, completed. */
  state: string | null
}

export interface DeskFinding {
  id: string
  project: string
  /** The Supabase ref, which is what groups findings by project; the name when a row has none. */
  projectRef: string
  check: string
  /** The check's one-line description, or its name when the project sent none. */
  title: string
  category: string
  severity: string | null
  severityRank: number
  /** "9 rows" for a data check, "22 objects" for a security one, "1 table" for freshness. */
  countLabel: string | null
  objects: FindingObject[]
  sourceQuery: string | null
  error: string | null
  firstSeenAt: string
  lastSeenAt: string
  clearedAt: string | null
  reportsSeen: number
  state: FindingState
  ticket: FindingTicket | null
  approvalId: string | null
  decidedBy: string | null
  decisionNote: string | null
}

const RANK: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 }

/** The CSS custom property a severity is written in. Muted families only. */
export function severityTone(severity: string | null): string {
  if (severity === 'critical' || severity === 'high') return 'var(--s-fail)'
  if (severity === 'medium') return 'var(--s-wait)'
  return 'var(--ink-3)'
}

const UNIT: Record<string, [string, string]> = {
  dq: ['row', 'rows'],
  sec: ['object', 'objects'],
  freshness: ['table', 'tables'],
}

function countLabel(category: string, count: number | null): string | null {
  if (count === null) return null
  const [one, many] = UNIT[category] ?? ['item', 'items']
  return `${count} ${count === 1 ? one : many}`
}

function toObject(value: Json): FindingObject | null {
  const o = obj(value)
  const name = o ? str(o.object_name) : null
  if (!o || !name) return null
  return { name, grantee: str(o.grantee), severity: str(o.severity), hint: str(o.remediation) }
}

function stateOf(row: DataFindingRow): FindingState {
  if (row.status === 'cleared') return row.cleared_reason === 'retired' ? 'retired' : 'cleared'
  return row.accepted ? 'accepted' : 'open'
}

/** Rows a view hands back, translated. A row missing its identity is dropped, not guessed at. */
export function toFindings(rows: ReadonlyArray<DataFindingRow>): DeskFinding[] {
  const out: DeskFinding[] = []
  for (const row of rows) {
    if (!row.id || !row.check_name || !row.project || !row.first_seen_at || !row.last_seen_at) continue
    const category = row.category ?? ''
    out.push({
      id: row.id,
      project: row.project,
      projectRef: row.project_ref || row.project,
      check: row.check_name,
      title: row.description?.trim() || row.check_name,
      category,
      severity: row.severity,
      severityRank: RANK[row.severity ?? ''] ?? 0,
      countLabel: row.outcome === 'error' ? null : countLabel(category, row.violation_count),
      objects: arr(row.objects).map(toObject).filter((o): o is FindingObject => o !== null),
      sourceQuery: row.source_query,
      error: row.error,
      firstSeenAt: row.first_seen_at,
      lastSeenAt: row.last_seen_at,
      clearedAt: row.cleared_at,
      reportsSeen: row.reports_seen ?? 1,
      state: stateOf(row),
      ticket: row.linear_ref ? { ref: row.linear_ref, url: row.linear_url, state: row.linear_state } : null,
      approvalId: row.approval_id,
      decidedBy: row.decided_by,
      decisionNote: row.decision_note,
    })
  }
  return out
}

export interface SeverityCounts {
  critical: number
  high: number
  medium: number
  low: number
}

export interface FindingsBoard {
  /** Worst first; within a severity, the longest-standing first. */
  open: DeskFinding[]
  accepted: DeskFinding[]
  /** Cleared or retired, most recent first. */
  closed: DeskFinding[]
  openBySeverity: SeverityCounts
  /** Open findings that have a Linear ticket. */
  ticketed: number
}

export function findingsBoard(findings: ReadonlyArray<DeskFinding>): FindingsBoard {
  const open = findings
    .filter((f) => f.state === 'open')
    .sort((a, b) => b.severityRank - a.severityRank || a.firstSeenAt.localeCompare(b.firstSeenAt))
  const accepted = findings.filter((f) => f.state === 'accepted')
  const closed = findings
    .filter((f) => f.state === 'cleared' || f.state === 'retired')
    .sort((a, b) => (b.clearedAt ?? '').localeCompare(a.clearedAt ?? ''))
  const openBySeverity: SeverityCounts = { critical: 0, high: 0, medium: 0, low: 0 }
  for (const f of open) {
    if (f.severity && f.severity in openBySeverity) openBySeverity[f.severity as keyof SeverityCounts] += 1
  }
  return { open, accepted, closed, openBySeverity, ticketed: open.filter((f) => f.ticket).length }
}

/** "2 critical · 1 medium" -- only the severities that have something in them. */
export function severityLine(counts: SeverityCounts): string {
  const parts = (['critical', 'high', 'medium', 'low'] as const)
    .filter((s) => counts[s] > 0)
    .map((s) => `${counts[s]} ${s}`)
  return parts.length ? parts.join(' · ') : 'nothing open'
}

// -- the projects that report ----------------------------------------------

/** The nightly lane's threshold: a day and a bit without a report is silence, not a quiet night. */
export const REPORT_STALE_HOURS = 26

export interface ProjectStanding {
  ref: string
  project: string
  lastReportAt: string
  /** No report inside REPORT_STALE_HOURS. The findings below it are then older than they look. */
  silent: boolean
  /** The project's own summary said its checks stopped running, whatever it reported. */
  checksOverdue: boolean
  /** The project's overall_status, in words rather than its colour name. */
  standing: string
  /** The reconciliation map its last report carried (contract v10), or null when it reconciles nothing. */
  recon: ReconMap | null
}

const STANDING: Record<string, string> = {
  red: 'something critical is open',
  amber: 'something needs a look',
  green: 'nothing open',
}

export function toProjectStanding(
  rows: ReadonlyArray<DataProjectHealthRow>,
  now: number = Date.now(),
): ProjectStanding[] {
  return rows.map((row) => {
    const summary = obj(row.summary)
    const status = summary ? str(summary.overall_status) : null
    const last = Date.parse(row.last_report_at)
    return {
      ref: row.project_ref,
      project: row.project,
      lastReportAt: row.last_report_at,
      silent: Number.isNaN(last) || now - last > REPORT_STALE_HOURS * 60 * 60_000,
      checksOverdue: summary?.checks_overdue === true,
      standing: (status && STANDING[status]) || 'no summary in its last report',
      recon: toReconMap(row.summary),
    }
  })
}

// -- one board per project -------------------------------------------------

export interface ProjectFindings {
  ref: string
  project: string
  /** Its last report, or null when it has findings but no health row (it should not happen; shown, not hidden). */
  standing: ProjectStanding | null
  board: FindingsBoard
}

/**
 * The desk's findings, split by the project that found them, so each database reads as
 * its own list with its own standing on top.
 *
 * Every project that reported appears, including one with nothing open -- that it
 * reported and found nothing is itself the fact. Worst open finding first, then the
 * most open, then by name, the same order the rows inside a board use.
 */
export function projectBoards(
  findings: ReadonlyArray<DeskFinding>,
  standings: ReadonlyArray<ProjectStanding>,
): ProjectFindings[] {
  const byRef = new Map<string, { project: string; findings: DeskFinding[] }>()
  for (const s of standings) byRef.set(s.ref, { project: s.project, findings: [] })
  for (const f of findings) {
    const entry = byRef.get(f.projectRef) ?? { project: f.project, findings: [] }
    entry.findings.push(f)
    byRef.set(f.projectRef, entry)
  }
  const standingByRef = new Map(standings.map((s) => [s.ref, s]))
  const out: ProjectFindings[] = [...byRef.entries()].map(([ref, e]) => ({
    ref,
    project: e.project,
    standing: standingByRef.get(ref) ?? null,
    board: findingsBoard(e.findings),
  }))
  const worst = (p: ProjectFindings) => p.board.open[0]?.severityRank ?? 0
  return out.sort((a, b) =>
    worst(b) - worst(a) || b.board.open.length - a.board.open.length || a.project.localeCompare(b.project))
}
